import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export type Theme = "light" | "dark";

interface ThemeState {
  state: Theme;
}

interface ThemeActions {
  set: (theme: Theme) => void;
  toggle: () => void;
}

export type ThemeStore = ThemeState & ThemeActions;

const STORAGE_KEY = "theme-preference";

function getSavedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw)?.state?.state ?? JSON.parse(raw)?.state;
    return value === "dark" || value === "light" ? value : null;
  } catch {
    return null;
  }
}

const initialTheme: Theme =
  getSavedTheme() ??
  (window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light");

document.documentElement.classList.toggle("dark", initialTheme === "dark");

const THEME_COLORS: Record<Theme, string> = {
  light: "#ffffff",
  dark: "#030712",
};

function updateThemeColor(theme: Theme) {
  const meta = document.getElementById(
    "theme-color-meta",
  ) as HTMLMetaElement | null;
  if (meta) {
    meta.content = THEME_COLORS[theme];
  }
}

updateThemeColor(initialTheme);

const toggleTheme = (theme: Theme) => (theme === "light" ? "dark" : "light");

export const useThemeStore = create<ThemeStore>()(
  devtools(
    persist(
      immer((set) => ({
        state: initialTheme,
        set: (theme) => {
          set((state) => {
            state.state = theme;
          });
        },
        toggle: () => {
          set((state) => {
            state.state = toggleTheme(state.state);
          });
        },
      })),
      { name: STORAGE_KEY },
    ),
    { name: "theme" },
  ),
);

// theme 변경 시 classList 및 theme-color meta 자동 동기화
useThemeStore.subscribe((state) => {
  document.documentElement.classList.toggle("dark", state.state === "dark");
  updateThemeColor(state.state);
});
